import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Box,
  Card,
  CardContent,
  TextField,
  Link,
  Alert,
} from '@mui/material';
import PrimaryButton from './components/PrimaryButton';
import GoogleIcon from '@mui/icons-material/Google';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from './firebaseConfig';
import routes from './routes';

const Homepage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error(err);
      setError("Email ou senha incorretos. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Header */}
      <AppBar position="fixed" color="default" elevation={0} sx={{ backgroundColor: 'white' }}>
        <Toolbar sx={{ justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Agreedly
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <PrimaryButton variant="text" onClick={() => navigate(routes.HOME)}>
              Entrar
            </PrimaryButton>
            <PrimaryButton onClick={() => navigate(routes.SIGNUP)}>
              Crie Sua Conta
            </PrimaryButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          textAlign: 'center',
          py: 10,
          mt: 8,
          backgroundImage: 'url(https://via.placeholder.com/1920x600)',
          backgroundSize: 'cover',
          color: 'white',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" gutterBottom>
            Facilite seus Contratos Digitais
          </Typography>
          <Typography variant="h6" component="p" sx={{ mb: 4 }}>
            Uma plataforma simples e segura para criar, assinar e gerenciar contratos.
          </Typography>
          <PrimaryButton size="large" sx={{ mr: 2 }} onClick={() => navigate(routes.SIGNUP)}>
            Comece Agora
          </PrimaryButton>
          <PrimaryButton variant="outlined" size="large" color="inherit" onClick={() => navigate(routes.SIGNUP)}>
            Saiba Mais
          </PrimaryButton>
        </Container>
      </Box>

      {/* Login Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom>
              Bem-vindo ao Agreedly
            </Typography>
            <Typography>
              Nossa plataforma permite que você gerencie todos os seus contratos de forma simples e segura. Faça login para acessar sua conta ou crie uma nova conta para começar.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box component="form" onSubmit={handleEmailLogin}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Senha"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Link href="#" variant="body2">
                  Esqueci minha senha
                </Link>
              </Box>
              {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {error}
                </Alert>
              )}
              <PrimaryButton type="submit" fullWidth sx={{ mt: 1 }} isLoading={isLoading}>
                Entrar
              </PrimaryButton>
              <PrimaryButton fullWidth variant="outlined" sx={{ mt: 1 }} startIcon={<GoogleIcon />} >
                Entrar com Google
              </PrimaryButton>
              <PrimaryButton fullWidth variant="outlined" sx={{ mt: 1 }} onClick={() => navigate(routes.SIGNUP)}>
                Criar Conta
              </PrimaryButton>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Target Audience Section */}
      <Box sx={{ backgroundColor: 'white', py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" gutterBottom>
            Quem pode se beneficiar do Agreedly?
          </Typography>
          <Typography variant="body1" align="center" sx={{ mb: 6 }}>
            Nossa plataforma é ideal para diversos perfis de usuários.
          </Typography>
          <Grid container spacing={4}>
            {[
              {
                title: 'Profissionais Autônomos e Freelancers',
                description: 'Trabalham em regime de prestação de serviços e precisam de contratos simples, rápidos e acessíveis para formalizar acordos com clientes.',
                examples: 'Designers, desenvolvedores, redatores, fotógrafos, consultores.'
              },
              {
                title: 'Pequenas e Médias Empresas (PMEs)',
                description: 'Gerenciam contratos com fornecedores, parceiros e clientes, sem departamento jurídico ou recursos para ferramentas complexas e caras.',
                examples: 'Agências de marketing, lojas online, startups, empresas de serviços locais.'
              },
              {
                title: 'Coaches e Consultores',
                description: 'Trabalham diretamente com clientes e oferecem serviços contínuos ou baseados em pacotes, formalizando acordos sobre escopo, cronograma e pagamentos.',
                examples: 'Coaches de carreira, nutricionistas, treinadores fitness.'
              },
              {
                title: 'Times de Recursos Humanos',
                description: 'Formalizam contratos de trabalho, acordos temporários ou termos de confidencialidade, buscando automação para reduzir o tempo de gerenciamento de documentos.',
                examples: 'RH de startups, empresas com funcionários remotos.'
              },
              {
                title: 'Jovens Empreendedores (Geração Z e Millennials)',
                description: 'Preferem plataformas digitais e intuitivas, buscando soluções acessíveis e rápidas para formalizar contratos de forma prática e eficiente.',
                examples: 'Criadores de conteúdo, empreendedores digitais.'
              },
              {
                title: 'Prestadores de Serviços Locais',
                description: 'Atuam diretamente com o público, formalizando acordos com clientes para serviços pontuais, simplificando a criação de contratos sem depender de advogados.',
                examples: 'Eletricistas, encanadores, motoristas, personal organizers.'
              },
              {
                title: 'Advogados e Escritórios de Advocacia',
                description: 'Precisam de uma plataforma para atender clientes que demandam contratos digitais, centralizando contratos e garantindo conformidade com as leis locais.',
                examples: 'Advogados independentes, escritórios de advocacia.'
              },
            ].map((audience, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card elevation={3} sx={{ textAlign: 'center', p: 2 }}>
                  <CardContent>
                    <Typography variant="h6" component="div" gutterBottom>
                      {audience.title}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {audience.description}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Exemplos: {audience.examples}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Por que escolher o Agreedly?
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 6 }}>
          Tudo que você precisa para gerenciar seus contratos em um só lugar.
        </Typography>
        <Grid container spacing={4}>
          {[
            { title: 'Assinaturas com Validade Jurídica', description: 'Assine documentos de forma segura e reconhecida.' },
            { title: 'Organização Simplificada', description: 'Armazene e gerencie contratos em um único painel.' },
            { title: 'Acesso em Todos os Dispositivos', description: 'Acompanhe tudo pelo celular ou desktop.' },
          ].map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card elevation={3} sx={{ textAlign: 'center', p: 2 }}>
                <CardContent>
                  <Typography variant="h6" component="div" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2">{feature.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Testimonials Section */}
      <Box sx={{ py: 10, backgroundColor: '#f9f9f9' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" gutterBottom>
            O que nossos clientes dizem
          </Typography>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            {[
              {
                name: 'Ana Maria',
                feedback: 'A plataforma tornou minha gestão de contratos muito mais simples!',
              },
              {
                name: 'Carlos Alberto',
                feedback: 'Rápido, seguro e fácil de usar. Recomendo a todos!'
              },
            ].map((testimonial, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card elevation={3}>
                  <CardContent>
                    <Typography variant="body1" gutterBottom>
                      "{testimonial.feedback}"
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary">
                      - {testimonial.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Perguntas Frequentes
        </Typography>
        <Grid container spacing={4} sx={{ mt: 4 }}>
          {[
            { question: 'Como faço para criar uma conta?', answer: 'Clique no botão "Criar Conta" e siga as instruções.' },
            { question: 'Posso acessar a plataforma pelo celular?', answer: 'Sim, nossa plataforma é responsiva e pode ser acessada de qualquer dispositivo.' },
            { question: 'Os contratos são seguros?', answer: 'Sim, utilizamos criptografia para garantir a segurança dos seus documentos.' },
          ].map((faq, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" component="div" gutterBottom>
                    {faq.question}
                  </Typography>
                  <Typography variant="body2">{faq.answer}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call to Action Section */}
      <Box sx={{ py: 10, textAlign: 'center', backgroundColor: '#1976d2', color: 'white' }}>
        <Typography variant="h4" component="h2" gutterBottom>
          Pronto para começar?
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          Crie sua conta agora e aproveite os benefícios do Agreedly.
        </Typography>
        <PrimaryButton size="large" sx={{ mr: 2 }} onClick={() => navigate(routes.SIGNUP)}>
          Criar Conta
        </PrimaryButton>
        <PrimaryButton variant="outlined" size="large" color="inherit" onClick={() => navigate(routes.CONTACT)}>
          Fale Conosco
        </PrimaryButton>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, textAlign: 'center', backgroundColor: '#333', color: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="body1">
            &copy; 2025 Agreedly. Todos os direitos reservados.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <Link href="#" color="inherit" underline="none">
              Termos de Serviço
            </Link>
            {' | '}
            <Link href="#" color="inherit" underline="none">
              Política de Privacidade
            </Link>
          </Typography>
        </Container>
      </Box>
    </>
  );
};

export default Homepage;